import { logger } from '@adornis/base/logging.js';
import { type ValueEvent } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import '@adornis/datetime/x-timerange-picker.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import '@adornis/forms/x-input.js';
import { createFallbackSlotAtNewTime } from '@ils/slots/api/slot.js';
import type { ExamSlotGroup } from '@ils/slots/db/examSlotGroup.js';
import { Slot } from '@ils/slots/db/slot.js';
import { html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { state } from 'lit/decorators/state.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import { combineLatest } from 'rxjs/internal/observable/combineLatest';
import { filter, map, switchMap } from 'rxjs/operators';
import type { ExamDay } from '../../db/examdays.js';
import { ILSUser } from '../../db/ils-user.js';

@customElement('ils-create-fallback-slot')
export class ILSCreateFallbackSlot extends XDialog<boolean> {
  static override get element_name() {
    return 'ils-create-fallback-slot';
  }
  override styles() {
    return [
      {
        '#free-slot': { cursor: 'pointer' },
        '#free-slot:hover': { background: this.colors.neutral.shade(200) },
        '#free-slot:active': { background: this.colors.neutral.shade(400) },
      },
    ];
  }
  @state() examDay = new RXController<ExamDay | undefined>(this, undefined);
  @state() examSlots = new RXController<ExamSlotGroup[] | undefined>(this, undefined);
  @state() failedSlot = new RXController<Slot | undefined>(this, undefined);
  @state() private readonly _candidate = new RXController(
    this,
    this.failedSlot.observable.pipe(
      filter(Boolean),
      switchMap(slot => getByID(ILSUser, slot.candidateID)({ ecommerce: { firstName: 1, lastName: 1 } })),
    ),
  );
  @state() private readonly _freeTimeSlots = new RXController(
    this,
    combineLatest([this.examSlots.observable, this.examDay.observable, this._candidate.observable]).pipe(
      map(([slots, examDay, candidate]) => {
        if (!examDay || !slots || !candidate) return [];
        const freeTimeSlots: Array<{ start: DateTime; end: DateTime }> = [];

        const nonFallbackExamGroupSlots = slots.filter(
          slotGroup => !slotGroup.slots.every(slot => slot.isFallbackSlot),
        );

        for (let i = 0; i < nonFallbackExamGroupSlots.length; i++) {
          const slotGroup = nonFallbackExamGroupSlots[i]!;
          const nonFallbackSlots = slotGroup.slots.filter(slot => !slot.isFallbackSlot);

          if (nonFallbackSlots[0]?.osce?.taskType === 'text') {
            freeTimeSlots.push({
              start: nonFallbackSlots.every(slot => slot.candidateID !== candidate._id)
                ? slotGroup.startTime.plus({ minutes: 1 })
                : slotGroup.endTime.plus({ minutes: 3 }),
              end: (nonFallbackExamGroupSlots[i + 1]?.startTime ?? examDay.end).minus({ minutes: 3 }),
            });
          }
        }

        return freeTimeSlots;
      }),
    ),
  );

  private _pickedStartTime?: DateTime;
  private _pickedEndTime?: DateTime;

  private get _futureFreeTimeSlots() {
    return (this._freeTimeSlots.value || [])
      .filter(
        timeSlot =>
          DateTime.now().set({ hour: timeSlot.end.hour, minute: timeSlot.end.minute, second: 0 }) > DateTime.now(),
      )
      .filter(timeSlot => {
        // See if this timeSlot has enough time to fit in selected OSCE + time to prepare fallback
        // note: x / 60000 converts ms to minutes
        if (!this.failedSlot.value?.osce) return false;

        const preparationTime = 5; // 5 minutes
        const startInMin = timeSlot.start.toMillis() / 60000;
        const endInMin = timeSlot.end.toMillis() / 60000;

        return endInMin - startInMin >= this.failedSlot.value.osce.durationInMinutes + preparationTime;
      });
  }

  override render() {
    if (!this.examDay.value) return this.errorContent('Der ExamDay konnte nicht gefunden werden.');
    if (!this.examSlots.value) return this.errorContent('Es wurden keine Slots angegeben.');
    if (!this.failedSlot.value) return this.errorContent('Es wurde kein fehlgeschlagener Slot angegeben.');
    if (!this._candidate.value) return this.errorContent('Der Prüfling konnte nicht gefunden werden.');

    const examDay = this.examDay.value;
    const slots = this.examSlots.value;
    const failedSlot = this.failedSlot.value;

    const lastEndTime = slots[slots.length - 1]!.endTime;
    const newLastStart = lastEndTime.plus({ minute: examDay.slotPause });
    const newLastEnd = newLastStart.plus({ minute: failedSlot.osce?.durationInMinutes });

    const endTimes: DateTime[] = [];
    for (let i = 0; i < 5; i++) endTimes.push(newLastEnd.plus({ minute: 5 * i }));

    return html`
      <x-flex padding="lg" space="md" ${css({ minWidth: '300px' })}>
        ${this.failedSlot.value.isFallbackSlot ? this._editExistingFallbackSlot() : this._createNewFallbackSlot()}
        <x-flex horizontal space="sm" end>
          <x-button mode="outline" tone="error" @click=${() => this.close(false)} ${css({ marginLeft: 'auto' })}>
            Abbrechen
          </x-button>
          <x-button @click=${() => this.createOrSaveFallbackSlot()}>
            Fallback ${this.failedSlot.value.isFallbackSlot ? 'Verlegen' : 'Erstellen'}
          </x-button>
        </x-flex>
      </x-flex>
    `;
  }

  private _createNewFallbackSlot() {
    if (!this._candidate.value) return html` <x-text> Kein Kandidat gegeben </x-text> `;

    return html`
      <x-h2> Slot wiederholen von ${this._candidate.value.ecommerce?.fullName} </x-h2>
      ${this._failedSlotInfo()} ${this._freeTimeSlotsDisplay()}
      <x-flex space="sm">
        <x-text> Uhrzeit auswählen: </x-text>
        <x-flex horizontal crossaxis-end space="sm">
          <x-input
            type="time"
            placeholder="von"
            placeholder-mode="inline-floating"
            @value-changed=${(e: ValueEvent<DateTime>) => (this._pickedStartTime = e.detail.value)}
            .value=${this._futureFreeTimeSlots.find(
              timeslot => Math.ceil(timeslot.end.diff(timeslot.start, 'minutes').minutes) >= 10,
            )?.start}
          ></x-input>
          <x-input
            type="time"
            placeholder="bis"
            placeholder-mode="inline-floating"
            @value-changed=${(e: ValueEvent<DateTime>) => (this._pickedEndTime = e.detail.value)}
            .value=${this._futureFreeTimeSlots
              .find(timeslot => Math.ceil(timeslot.end.diff(timeslot.start, 'minutes').minutes) >= 10)
              ?.start.plus({ minutes: 10 })}
          ></x-input>
        </x-flex>
      </x-flex>
    `;
  }

  private _editExistingFallbackSlot() {
    return html`
      <x-h2> Fallback Slot verlegen </x-h2>
      ${this._failedSlotInfo()} ${this._freeTimeSlotsDisplay()}

      <x-flex space="sm">
        <x-text> Uhrzeit auswählen: </x-text>
        <x-flex horizontal crossaxis-end space="sm">
          <x-input
            type="time"
            placeholder="von"
            placeholder-mode="inline-floating"
            @value-changed=${(e: ValueEvent<DateTime>) => (this._pickedStartTime = e.detail.value)}
            .value=${this.failedSlot.value!.startTime}
          ></x-input>
          <x-input
            type="time"
            placeholder="bis"
            placeholder-mode="inline-floating"
            @value-changed=${(e: ValueEvent<DateTime>) => (this._pickedEndTime = e.detail.value)}
            .value=${this.failedSlot.value!.endTime ?? this.failedSlot.value!.startTime.plus({ minutes: 10 })}
          ></x-input>
        </x-flex>
      </x-flex>
    `;
  }

  private _failedSlotInfo() {
    return html`
      <x-h3>
        "${this.failedSlot.value!.osce?.title}" um ${this.failedSlot.value!.startTime.toFormat('HH:mm')} -
        ${this.failedSlot.value!.endTime?.toFormat('HH:mm') ?? ''}
      </x-h3>
    `;
  }

  private _freeTimeSlotsDisplay() {
    return html`
      <x-flex space="sm">
        <x-text bold> Grundsätzlich verfügbare Zeiten sind: </x-text>
        ${this._futureFreeTimeSlots.length
          ? repeat(
              this._futureFreeTimeSlots,
              freeTimeSlot =>
                html`
                  <x-text>
                    ${freeTimeSlot.start.toLocaleString(DateTime.TIME_24_SIMPLE)} -
                    ${freeTimeSlot.end.toLocaleString(DateTime.TIME_24_SIMPLE)}
                  </x-text>
                `,
            )
          : html` <x-text tone="subtle"> Keine verfügbare Zeiten </x-text> `}
      </x-flex>
    `;
  }

  private async createOrSaveFallbackSlot() {
    if (!this.examDay.value || !this.failedSlot.value) return;
    if (!this._pickedStartTime || !this._pickedEndTime)
      return XDialog.alert('Bitte gib zuerst eine Start- und Enduhrzeit aus!');

    const isFallback = this.failedSlot.value.isFallbackSlot;
    const confirm = await XDialog.confirm(
      isFallback
        ? 'Den Fallback Slot verlegen?'
        : 'Den Fallback slot wirklich anlegen? Dies kann man nicht mehr Rückgängig machen!',
    );
    if (confirm) {
      try {
        if (isFallback) {
          this.failedSlot.value.startTime = this._pickedStartTime;
          this.failedSlot.value.endTime = this._pickedEndTime;
          await XDialog.waitFor(this.failedSlot.value.save(), 'Fallback-Slot wird verlegt');
          XSnackbar.show('Fallback-Slot wurde verschoben.');
        } else {
          await XDialog.waitFor(
            createFallbackSlotAtNewTime(
              this.examDay.value._id,
              this.failedSlot.value._id,
              this._pickedStartTime,
              this._pickedEndTime,
            )({}),
            'Neuer Fallback-Slot wird erstellt..',
          );
          XSnackbar.show('Fallback-slot wurde erstellt.');
        }
        this.close(true);
      } catch (e) {
        logger.error(e);
        XDialog.alert(
          (isFallback
            ? 'Beim verlegen des Fallbacks ist etwas schief gelaufen: '
            : 'Beim erstellen des Fallbacks ist etwas schief gelaufen: ') + (e as Error).message,
        );
      }
    }
  }

  private errorContent(message: string) {
    return html`
      <x-flex padding="lg" space="md">
        <x-text> ${message} </x-text>
        <x-button @click=${() => this.close(false)} ${css({ marginLeft: 'auto' })}> OK </x-button>
      </x-flex>
    `;
  }
}
